import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import { getTickets, updateTicket } from '../../services/ticketService';
import TicketDetailDialog from './TicketDetailDialog';
import Pagination from '../../components/Pagination';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Chip, Box, Typography, CircularProgress, Snackbar, IconButton
} from '@material-ui/core';
import {  makeStyles , useTheme } from "@material-ui/core/styles";
import VisibilityIcon from '@material-ui/icons/Visibility';
import ReplyIcon from '@material-ui/icons/Reply';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
  },
  actionBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': { backgroundColor: theme.palette.action.hover, color: theme.palette.text.primary },
  },
  countChip: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.85rem',
    height: 36,
    padding: '0 8px',
    borderRadius: 10,
  },
}));

const statusColors = {
  open: '#ef4444',
  closed: '#64748b',
  in_progress: '#f59e0b',
};

export default function TicketListPage() {
  const classes = useStyles();
  
  const theme = useTheme();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const loadData = useCallback(async (opts = {}) => {
    setLoading(true);
    const targetPage = opts.page !== undefined ? opts.page : 1;
    try {
      const result = await getTickets({ page: targetPage });
      setTickets(result.items);
      setTotalCount(result.total);
      setTotalPages(result.totalPages);
      setPage(result.page);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load tickets' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData({ page: 1 }); }, [loadData]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadData({ page: newPage });
  };

  const handleView = (ticket) => {
    setSelectedTicket(ticket);
    setDetailOpen(true);
  };

  const handleSaveReply = async (id, data) => {
    try {
      await updateTicket(id, data);
      setSnackbar({ open: true, message: 'Ticket updated successfully!' });
      setDetailOpen(false);
      loadData({ page });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to update ticket' });
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString();
  };

  return (
    <AdminLayout title="Support Tickets">
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="body2" color="textSecondary">
          Review, assign, and resolve user support tickets.
        </Typography>
        <Box display="flex" style={{ gap: 16 }} alignItems="center">
          <Chip label={`Total Tickets: ${totalCount}`} className={classes.countChip} />
          <Button variant="outlined" onClick={() => loadData({ page })} color="default">
            Refresh
          </Button>
        </Box>
      </Box>

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <Typography color="textSecondary" style={{ marginTop: 16 }}>Loading tickets...</Typography>
        </div>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography style={{ color: theme.palette.text.secondary, padding: 24 }}>No tickets yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tickets.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Typography color="textPrimary" style={{ fontSize: '0.9rem' }}>
                        {t.userName || 'Anonymous'}
                      </Typography>
                      <Typography style={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                        {t.userEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color="textSecondary" style={{ fontSize: '0.85rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.subject || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.status || 'open'}
                        size="small"
                        style={{
                          backgroundColor: `${statusColors[t.status] || '#64748b'}20`,
                          color: statusColors[t.status] || '#94a3b8',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography color="textSecondary" style={{ fontSize: '0.85rem' }}>
                        {formatDate(t.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton className={classes.actionBtn} onClick={() => handleView(t)}>
                        {t.adminReply ? <ReplyIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && totalCount > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={20}
          onPageChange={handlePageChange}
        />
      )}

      {selectedTicket && (
        <TicketDetailDialog
          open={detailOpen}
          ticket={selectedTicket}
          onClose={() => setDetailOpen(false)}
          onSave={handleSaveReply}
        />
      )}

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </AdminLayout>
  );
}
